import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Signup from "@/pages/Signup";
import Onboarding from "@/pages/Onboarding";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import Settings from "@/pages/Settings";
import SuperAdmin from "@/pages/SuperAdmin";
import AdminLeadHub from "@/pages/AdminLeadHub";
import AdminCoverage from "@/pages/AdminCoverage";
import AdminConversion from "@/pages/AdminConversion";
import AdminEconomics from "@/pages/AdminEconomics";
import AdminEmailDiagnostics from "@/pages/AdminEmailDiagnostics";
import AdminSupport from "@/pages/AdminSupport";
import AdminSubscriptions from "@/pages/AdminSubscriptions";
import AdminOutreach from "@/pages/AdminOutreach";
import Sources from "@/pages/Sources";
import BillingHistory from "@/pages/BillingHistory";
import Invite from "@/pages/Invite";
import ReadyToBook from "@/pages/ReadyToBook";
import SaveCardReturn from "@/pages/SaveCardReturn";
import PublicEstimate from "@/pages/PublicEstimate";
import Billing from "@/pages/Billing";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Contact from "@/pages/Contact";
import BrandPreview from "@/pages/BrandPreview";
import ForScreenCompanies from "@/pages/ForScreenCompanies";
import WalletReturn from "@/pages/WalletReturn";
import SeoPage from "@/pages/seo/SeoPage";
import HubLocations from "@/pages/seo/HubLocations";
import HubServices from "@/pages/seo/HubServices";

/** Reset scroll to the top whenever the route changes. Without this, clicking
 * an internal link from a scrolled-down page lands the user mid-page on the
 * new route (React Router does not restore scroll by default). */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function AdminGate() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "platform_admin") return <Navigate to="/dashboard" replace />;
  return <SuperAdmin />;
}

function AdminLeadHubGate() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "platform_admin") return <Navigate to="/dashboard" replace />;
  return <AdminLeadHub />;
}

function AdminCoverageGate() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "platform_admin") return <Navigate to="/dashboard" replace />;
  return <AdminCoverage />;
}

function AdminConversionGate() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "platform_admin") return <Navigate to="/dashboard" replace />;
  return <AdminConversion />;
}

function AdminEconomicsGate() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "platform_admin") return <Navigate to="/dashboard" replace />;
  return <AdminEconomics />;
}

function AdminEmailDiagGate() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "platform_admin") return <Navigate to="/dashboard" replace />;
  return <AdminEmailDiagnostics />;
}

function AdminSupportGate() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "platform_admin") return <Navigate to="/dashboard" replace />;
  return <AdminSupport />;
}

function CompanyBillingHistoryGate() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "platform_admin") return <Navigate to="/admin/transactions" replace />;
  return <BillingHistory mode="company" />;
}

function AdminTransactionsGate() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "platform_admin") return <Navigate to="/dashboard" replace />;
  return <BillingHistory mode="admin" />;
}

function AdminSubscriptionsGate() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "platform_admin") return <Navigate to="/dashboard" replace />;
  return <AdminSubscriptions />;
}

function InviteRedirect() {
  const { token } = useParams();
  return <Navigate to={`/invite/${token}`} replace />;
}

function AdminOutreachGate() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "platform_admin") return <Navigate to="/dashboard" replace />;
  return <AdminOutreach />;
}

function AdminSourcesGate() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "platform_admin") return <Navigate to="/dashboard" replace />;
  return <Sources mode="admin" />;
}

function CompanySourcesGate() {
  const { user, loading } = useAuth();
  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "platform_admin") return <Navigate to="/admin/sources" replace />;
  return <Sources mode="company" />;
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/for-screen-companies" element={<ForScreenCompanies />} />
              <Route path="/grow-your-screen-company" element={<ForScreenCompanies />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
              <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/leads" element={<ProtectedRoute><Leads /></ProtectedRoute>} />
              <Route path="/leads/:id" element={<ProtectedRoute><LeadDetail /></ProtectedRoute>} />
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/admin" element={<AdminGate />} />
              <Route path="/admin/leads" element={<AdminLeadHubGate />} />
              <Route path="/admin/coverage" element={<AdminCoverageGate />} />
              <Route path="/admin/conversion" element={<AdminConversionGate />} />
              <Route path="/admin/economics" element={<AdminEconomicsGate />} />
              <Route path="/admin/email" element={<AdminEmailDiagGate />} />
              <Route path="/admin/support" element={<AdminSupportGate />} />
              <Route path="/admin/support/:threadId" element={<AdminSupportGate />} />
              <Route path="/admin/transactions" element={<AdminTransactionsGate />} />
              <Route path="/admin/subscriptions" element={<AdminSubscriptionsGate />} />
              <Route path="/admin/outreach" element={<AdminOutreachGate />} />
              <Route path="/admin/sources" element={<AdminSourcesGate />} />
              <Route path="/sources" element={<CompanySourcesGate />} />
              <Route path="/billing/history" element={<CompanyBillingHistoryGate />} />
              <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
              <Route path="/marketplace/wallet/return" element={<ProtectedRoute><WalletReturn /></ProtectedRoute>} />
              <Route path="/marketplace/wallet/save-card/return" element={<ProtectedRoute><SaveCardReturn /></ProtectedRoute>} />
              <Route path="/billing/return" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/contact" element={<Contact />} />
              {/* Lead invite claim page — public, no auth */}
              <Route path="/invite/:token" element={<Invite />} />
              {/* Backwards-compat: an earlier admin share-link generator produced /invites/{token} (plural). Redirect any existing links so companies aren't sent to the 404. */}
              <Route path="/invites/:token" element={<InviteRedirect />} />
              {/* Homeowner "Ready to Book" landing — hit from the quote-confirmation email */}
              <Route path="/q/ready/:leadId" element={<ReadyToBook />} />
              {/* Internal-only branding mocks. Not linked from the site. */}
              <Route path="/brand-preview" element={<BrandPreview />} />
              {/* Canonical homeowner funnel */}
              <Route path="/quote" element={<PublicEstimate />} />
              {/* Legacy /platform/quote — redirect to canonical /quote */}
              <Route path="/platform/quote" element={<Navigate to="/quote" replace />} />
              <Route path="/platform/estimate" element={<Navigate to="/quote" replace />} />
              {/* Existing tenant routes — reserved second-segment names "quote" / "estimate" */}
              {/* Note: real company slugs hit these. The "platform" slug is now served via the canonical /quote route above. */}
              <Route path="/:slug/quote" element={<PublicEstimate />} />
              <Route path="/:slug/estimate" element={<PublicEstimate />} />
              {/* SEO hub pages — must come before SEO catch-all */}
              <Route path="/locations" element={<HubLocations />} />
              <Route path="/services" element={<HubServices />} />
              {/* SEO programmatic content. 1, 2, or 3 path segments. */}
              <Route path="/:p1" element={<SeoPage />} />
              <Route path="/:p1/:p2" element={<SeoPage />} />
              <Route path="/:p1/:p2/:p3" element={<SeoPage />} />
            </Routes>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
